import { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { todayKST } from '../../../utils/date';

const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '';
const parse = (s) => Number(String(s).replace(/,/g, '')) || 0;

export function usePayrollProfileHistory({ payItems, setForm, setItemValues, showToast }) {
  const [profileHistory, setProfileHistory] = useState([]);

  const buildItemPayload = (itemValues) =>
    itemValues.map((v) => ({
      pay_item_id: v.pay_item_id,
      item_name: v.item_name,
      amount: parse(v.amount),
      is_enabled: v.is_enabled !== false,
    }));

  const buildProfileHistoryBody = ({ form, body, itemValues }) => ({
    effective_from: form.payroll_effective_from || todayKST(),
    change_mode: 'manual',
    reason: form.payroll_change_reason || '',
    pay_type: body.pay_type,
    base_salary: body.base_salary,
    national_pension_yn: body.national_pension_yn,
    health_insurance_yn: body.health_insurance_yn,
    employment_ins_yn: body.employment_ins_yn,
    dependents_count: body.dependents_count,
    probation_months: body.probation_months,
    probation_ratio: body.probation_ratio,
    probation_period_unit: body.probation_period_unit === 'day' ? 'day' : 'month',
    memo: body.memo,
    item_values: buildItemPayload(itemValues),
  });

  const fetchProfileHistory = async (profileId) => {
    const historyRes = await apiClient.get(`/api/admin/payroll/employees/${profileId}/profile-history`);
    const rows = historyRes.ok ? (historyRes.data?.data || []) : [];
    setProfileHistory(rows);
    return rows;
  };

  const loadHistoryToForm = (row) => {
    setForm((prev) => ({
      ...prev,
      pay_type: row.pay_type || 'monthly',
      base_salary: fmt(row.base_salary),
      national_pension_yn: row.national_pension_yn !== false,
      health_insurance_yn: row.health_insurance_yn !== false,
      employment_ins_yn: row.employment_ins_yn !== false,
      dependents_count: String(row.dependents_count || 1),
      probation_months: String(row.probation_months || 0),
      probation_pct: String(Math.round((Number(row.probation_ratio) || 0.9) * 100)),
      probation_period_unit: row.probation_period_unit === 'day' ? 'day' : 'month',
      memo: row.memo || '',
      payroll_effective_from: row.effective_from || todayKST(),
      payroll_change_reason: row.reason || '',
    }));
    const existMap = {};
    (row.item_values || []).forEach((v) => { existMap[Number(v.pay_item_id)] = v; });
    setItemValues(payItems.map((i) => ({
      pay_item_id: i.id,
      item_name: i.name,
      calc_type: i.calc_type,
      amount: existMap[i.id] ? fmt(existMap[i.id].amount) : '',
      is_enabled: existMap[i.id] ? existMap[i.id].is_enabled !== false : true,
    })));
  };

  // 이력 삭제 — 미래(예약)/과거(적용분) 모두. applied 여부로 확인 문구 분기.
  //   백엔드가 안전장치(최소 1 적용분 보호)로 400 거부 시 그 메시지를 그대로 노출.
  const deleteHistory = async (profileId, row) => {
    if (!profileId || !row) return;
    const isApplied = String(row.effective_from || '') <= todayKST();
    const msg = isApplied
      ? '이미 적용된 급여정보를 삭제하면 이후 급여 계산이 직전 이력 기준으로 바뀝니다.\n(이미 확정된 급여명세는 변경되지 않습니다.)\n삭제할까요?'
      : '이 급여정보 예약을 취소할까요?';
    if (!window.confirm(msg)) return;
    const res = await apiClient.delete(`/api/admin/payroll/employees/${profileId}/profile-history/${row.id}`);
    if (res.ok) {
      setProfileHistory(res.data?.data?.history || []);
      showToast(
        isApplied
          ? (res.data?.data?.currentProfileResynced
              ? '이력을 삭제하고 현재 급여를 직전 이력으로 되돌렸습니다.'
              : '급여정보 이력을 삭제했습니다.')
          : '예약을 취소했습니다.',
        'success',
      );
    } else {
      showToast(res.data?.message || (isApplied ? '삭제 실패' : '예약 취소 실패'), 'error');
    }
  };

  return {
    profileHistory,
    setProfileHistory,
    fetchProfileHistory,
    buildItemPayload,
    buildProfileHistoryBody,
    loadHistoryToForm,
    deleteHistory,
  };
}
